import React, { Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { useFrameManager } from '@bcraft-frontend-lib/slots'
import block from 'bem-cn'
import { AnimatePresence, motion } from 'framer-motion'

import { mediaSize } from 'shared/style/var'

import { useDigitainSlotToken } from 'modules/SlotsRoot/hooks/useDigitainSlotToken'

import { changeOpenedLeftSidePanel, changeOpenedRightSidePanel } from 'features/sidePanel/actions'

import BonusPromotionNotification from 'components/BonusPromotionNotification/BonusPromotionNotification'
import { DefineDeviceComponent } from 'components/DefineDeviceComponent'
import { Footer } from 'components/Footer/Footer'
import { Header } from 'components/Header/Header'
import { DeviceDetect } from 'components/helpers/DeviceDetect/DeviceDetect'
import CashbackNotify from 'components/notifies/CashbackNotify/CashbackNotify'
import FirstDepositNotify from 'components/notifies/FirstDepositNotify/FirstDepositNotify'
import PersonalJackpotNotify from 'components/notifies/PersonalJackpotNotify/PersonalJackpotNotify'
import PageLoading from 'components/PageLoading/PageLoading'
import { AppGlobalPolling } from 'components/pollings/AppGlobalPolling'
import { SideMenu } from 'components/SideMenu/SideMenu'
import { SideMenuLeftContent } from 'components/SideMenu/SideMenuLeftContent'
import { SideMenuRightContent } from 'components/SideMenu/SideMenuRightContent'

import { getCurrenciesList } from './features/settings/actions'

import './App.scss'

const b = block('app')

interface IAppProps { }

const App: React.FC<IAppProps> = ({ children }) => {
  const dispatch = useDispatch()

  const { count: framesCount } = useFrameManager()
  const isFrameOpen = framesCount > 0

  const isMobileOrTablet = useMediaQuery({ maxWidth: mediaSize.laptop })

  const [isOpenLeftSideMenu, setIsOpenLeftSideMenu] = useState(false)
  const [isOpenRightSideMenu, setIsOpenRightSideMenu] = useState(false)

  const appContainerRef = useRef<HTMLDivElement>(null)

  const setLeftSideMenu = useCallback((val?: boolean) => {
    if (typeof val === 'boolean') setIsOpenLeftSideMenu(val)
    else setIsOpenLeftSideMenu(prev => !prev)
  }, [])
  const setRightSideMenu = useCallback((val?: boolean) => {
    if (typeof val === 'boolean') setIsOpenRightSideMenu(val)
    else setIsOpenRightSideMenu(prev => !prev)
  }, [])

  useLayoutEffect(() => {
    setTimeout(() => localStorage.removeItem('reloadCount'), 2000)
  }, [])

  useEffect(() => {
    if (!isMobileOrTablet) {
      dispatch(changeOpenedLeftSidePanel(false))
      dispatch(changeOpenedRightSidePanel(false))
    }
  }, [isMobileOrTablet, dispatch])

  useDigitainSlotToken()

  useEffect(() => {
    dispatch(getCurrenciesList())
  }, [])

  return (
    <div className={b()}>
      <CashbackNotify />
      <FirstDepositNotify />
      <PersonalJackpotNotify />
      <BonusPromotionNotification />
      <AppGlobalPolling />
      <div className={b('container')} ref={appContainerRef} id="app-container-id">
        <DeviceDetect />

        <DefineDeviceComponent
          mobileComponent={
            <>
              <SideMenu position="left" isOpen={isOpenLeftSideMenu} onToggleSideMenu={setLeftSideMenu}>
                <SideMenuLeftContent onClose={() => setLeftSideMenu(false)} />
              </SideMenu>
              <SideMenu position="right" isOpen={isOpenRightSideMenu} onToggleSideMenu={setRightSideMenu}>
                <SideMenuRightContent onClose={() => setRightSideMenu(false)} />
              </SideMenu>
            </>
          }
          desktopComponent={<></>}
        />

        <motion.div
          className={b('main-wrapper')}
          initial={{ x: 0 }}
          transition={{ duration: 0.35 }}>
          {!isFrameOpen && <Header onToggleLeftMenu={setLeftSideMenu} onToggleRightMenu={setRightSideMenu} />}

          <div className={b('main')}>
            <div className={b('main-inner', { isFrameOpen })}>

              <Suspense fallback={<PageLoading isLoading />}>
                <div className={b('main-content')}>
                  <AnimatePresence exitBeforeEnter>{children}</AnimatePresence>
                </div>
              </Suspense>
              <Footer scrollRef={appContainerRef} />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default App
